import { useState } from 'react';
import { useGameStore, useCurrentPlayer, useIsMyTurn } from '../../store/gameStore';
import { ResourceType } from '../../../shared/types';
import './ModernGameUI.css';

const RESOURCE_CONFIG: Record<ResourceType, { icon: string; name: string; color: string; bg: string }> = {
  brick: { icon: '🧱', name: 'レンガ', color: '#c0392b', bg: 'linear-gradient(135deg, #e74c3c, #c0392b)' },
  wood: { icon: '🪵', name: '木材', color: '#27ae60', bg: 'linear-gradient(135deg, #2ecc71, #27ae60)' },
  wheat: { icon: '🌾', name: '小麦', color: '#f39c12', bg: 'linear-gradient(135deg, #f1c40f, #f39c12)' },
  sheep: { icon: '🐑', name: '羊毛', color: '#16a085', bg: 'linear-gradient(135deg, #1abc9c, #16a085)' },
  ore: { icon: '⛏️', name: '鉱石', color: '#7f8c8d', bg: 'linear-gradient(135deg, #95a5a6, #7f8c8d)' },
};

const PHASE_NAMES: Record<string, string> = {
  waiting: '待機中',
  setup_round_1: 'セットアップ 1',
  setup_round_2: 'セットアップ 2',
  main: 'メインフェーズ',
  robber: '盗賊移動',
  discard: 'カード破棄',
  game_over: 'ゲーム終了',
};

export default function ModernGameUI() {
  const { gameState, gameId, playerId, selectedAction, setSelectedAction, sendAction } = useGameStore();
  const currentPlayer = useCurrentPlayer();
  const isMyTurn = useIsMyTurn();
  const [copied, setCopied] = useState(false);

  if (!gameState) return null;

  const turnPlayer = gameState.players[gameState.currentPlayerIndex];
  const isSetup = gameState.phase === 'setup_round_1' || gameState.phase === 'setup_round_2';
  const isMain = gameState.phase === 'main';
  const canRollDice = isMyTurn && isMain && !gameState.diceRoll;
  const canBuild = isMyTurn && isMain && gameState.diceRoll;
  const canEndTurn = isMyTurn && isMain && gameState.diceRoll;

  const handleCopyGameId = async () => {
    if (!gameId) return;
    try {
      await navigator.clipboard.writeText(gameId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      prompt('ゲームIDをコピー:', gameId);
    }
  };

  const handleStartGame = () => {
    sendAction({ type: 'start_game' });
  };

  const handleRollDice = () => {
    sendAction({ type: 'roll_dice' });
  };

  const handleEndTurn = () => {
    sendAction({ type: 'end_turn' });
    setSelectedAction(null);
  };

  const handleBuyDevCard = () => {
    sendAction({ type: 'buy_development_card' });
  };

  return (
    <div className="modern-ui">
      {/* ヘッダー */}
      <header className="ui-header">
        <div className="header-left">
          <div className="game-info">
            <span className="phase-badge">{PHASE_NAMES[gameState.phase] || gameState.phase}</span>
            {turnPlayer && (
              <span className="turn-info">
                <span 
                  className="turn-color" 
                  style={{ background: getPlayerColor(turnPlayer.color) }}
                />
                {turnPlayer.name}のターン
                {isMyTurn && <span className="your-turn">（あなた）</span>}
              </span>
            )}
          </div>
        </div>
        
        <div className="header-center">
          <h1 className="game-title">CATAN</h1>
        </div>
        
        <div className="header-right">
          <button className="game-id-btn" onClick={handleCopyGameId}>
            <span className="game-id-text">{gameId?.slice(0, 8)}...</span>
            <span className="copy-icon">{copied ? '✓' : '📋'}</span>
          </button>
        </div>
      </header>

      {/* 左サイドバー: プレイヤー情報 */}
      <aside className="sidebar-left">
        <div className="players-panel">
          <h3>プレイヤー</h3>
          {gameState.players.map((player) => (
            <div 
              key={player.id}
              className={`player-card ${player.id === turnPlayer?.id ? 'active' : ''} ${player.id === playerId ? 'self' : ''}`}
            >
              <div 
                className="player-avatar"
                style={{ background: getPlayerColor(player.color) }}
              >
                {player.name.charAt(0).toUpperCase()}
              </div>
              <div className="player-details">
                <div className="player-name">
                  {player.name}
                  {player.id === playerId && <span className="you-tag">YOU</span>}
                </div>
                <div className="player-stats">
                  <span title="勝利点">🏆{player.victoryPoints}</span>
                  <span title="資源">🃏{Object.values(player.resources).reduce((a, b) => a + b, 0)}</span>
                  <span title="発展カード">📜{player.developmentCards.length}</span>
                </div>
              </div>
              {player.id === turnPlayer?.id && <div className="turn-indicator">▶</div>}
            </div>
          ))}
        </div>

        {/* 特殊カード */}
        {(gameState.longestRoadPlayerId || gameState.largestArmyPlayerId) && (
          <div className="special-cards-panel">
            {gameState.longestRoadPlayerId && (
              <div className="special-card road">
                <span className="special-icon">🛤️</span>
                <span>最長交易路</span>
                <span className="special-holder">
                  {gameState.players.find(p => p.id === gameState.longestRoadPlayerId)?.name}
                </span>
              </div>
            )}
            {gameState.largestArmyPlayerId && (
              <div className="special-card army">
                <span className="special-icon">⚔️</span>
                <span>最大騎士力</span>
                <span className="special-holder">
                  {gameState.players.find(p => p.id === gameState.largestArmyPlayerId)?.name}
                </span>
              </div>
            )}
          </div>
        )}
      </aside>

      {/* 右サイドバー: サイコロ & アクション */}
      <aside className="sidebar-right">
        {/* サイコロ表示 */}
        {gameState.diceRoll && (
          <div className="dice-panel">
            <div className="dice-container">
              <div className="dice">{gameState.diceRoll[0]}</div>
              <div className="dice">{gameState.diceRoll[1]}</div>
            </div>
            <div className="dice-total">合計: {gameState.diceRoll[0] + gameState.diceRoll[1]}</div>
          </div>
        )}

        {/* アクションメニュー */}
        {isMyTurn && isMain && (
          <div className="action-panel">
            {canRollDice && (
              <button className="action-btn primary large" onClick={handleRollDice}>
                🎲 サイコロを振る
              </button>
            )}

            {canBuild && (
              <>
                <div className="action-group">
                  <h4>建設</h4>
                  <div className="action-buttons">
                    <button 
                      className={`action-btn ${selectedAction === 'road' ? 'selected' : ''}`}
                      onClick={() => setSelectedAction(selectedAction === 'road' ? null : 'road')}
                    >
                      🛤️ 道路
                      <span className="cost">🧱🪵</span>
                    </button>
                    <button 
                      className={`action-btn ${selectedAction === 'settlement' ? 'selected' : ''}`}
                      onClick={() => setSelectedAction(selectedAction === 'settlement' ? null : 'settlement')}
                    >
                      🏠 開拓地
                      <span className="cost">🧱🪵🌾🐑</span>
                    </button>
                    <button 
                      className={`action-btn ${selectedAction === 'city' ? 'selected' : ''}`}
                      onClick={() => setSelectedAction(selectedAction === 'city' ? null : 'city')}
                    >
                      🏰 都市
                      <span className="cost">🌾🌾⛏️⛏️⛏️</span>
                    </button>
                  </div>
                </div>

                <div className="action-group">
                  <h4>その他</h4>
                  <div className="action-buttons">
                    <button className="action-btn" onClick={handleBuyDevCard}>
                      📜 発展カード
                      <span className="cost">🌾🐑⛏️</span>
                    </button>
                    <button className="action-btn" onClick={() => console.log('Trade coming soon')}>
                      🔄 交易
                    </button>
                  </div>
                </div>
              </>
            )}

            {canEndTurn && (
              <button className="action-btn end-turn" onClick={handleEndTurn}>
                ✓ ターン終了
              </button>
            )}
          </div>
        )}

        {/* セットアップ中のヒント */}
        {isMyTurn && isSetup && (
          <div className="setup-hint">
            <h4>セットアップ</h4>
            <p>
              {gameState.phase === 'setup_round_1' 
                ? '開拓地を配置してください' 
                : '道路を配置してください'}
            </p>
            <p className="hint-detail">
              タイルの角をクリックして開拓地、<br />
              辺をクリックして道路を配置
            </p>
          </div>
        )}
      </aside>

      {/* 下部: 資源バー */}
      {currentPlayer && (
        <footer className="resource-footer">
          <div className="resource-bar">
            {(['brick', 'wood', 'wheat', 'sheep', 'ore'] as ResourceType[]).map(res => (
              <div 
                key={res}
                className="resource-card"
                style={{ '--res-bg': RESOURCE_CONFIG[res].bg } as React.CSSProperties}
              >
                <span className="resource-icon">{RESOURCE_CONFIG[res].icon}</span>
                <span className="resource-count">{currentPlayer.resources[res]}</span>
              </div>
            ))}
            
            {/* 発展カード */}
            {currentPlayer.developmentCards.length > 0 && (
              <div className="dev-cards-indicator" onClick={() => console.log('Dev cards:', currentPlayer.developmentCards)}>
                <span className="dev-icon">📜</span>
                <span className="dev-count">{currentPlayer.developmentCards.length}</span>
              </div>
            )}
          </div>
        </footer>
      )}

      {/* 待機中のスタートボタン */}
      {gameState.phase === 'waiting' && (
        <div className="waiting-overlay">
          <div className="waiting-content">
            <h2>プレイヤー待機中</h2>
            <p>{gameState.players.length} / {gameState.gameMode === 'extended' ? 6 : 4} 人</p>
            <div className="waiting-players">
              {gameState.players.map(p => (
                <div key={p.id} className="waiting-player" style={{ background: getPlayerColor(p.color) }}>
                  {p.name}
                </div>
              ))}
            </div>
            {gameState.players.length >= 2 && (
              <button className="start-btn" onClick={handleStartGame}>
                🎮 ゲームスタート
              </button>
            )}
          </div>
        </div>
      )}

      {/* ゲームオーバー */}
      {gameState.phase === 'game_over' && gameState.winner && (
        <div className="game-over-overlay">
          <div className="game-over-content">
            <h2>🎉 ゲーム終了！</h2>
            <div className="winner-announcement">
              <div 
                className="winner-avatar"
                style={{ background: getPlayerColor(gameState.players.find(p => p.id === gameState.winner)?.color || 'white') }}
              >
                👑
              </div>
              <h3>{gameState.players.find(p => p.id === gameState.winner)?.name} の勝利！</h3>
            </div>
            <button className="restart-btn" onClick={() => window.location.reload()}>
              🔄 新しいゲーム
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function getPlayerColor(color: string): string {
  const colors: Record<string, string> = {
    red: '#e74c3c',
    blue: '#3498db',
    orange: '#e67e22',
    white: '#ecf0f1',
    green: '#27ae60',
    brown: '#8b4513',
  };
  return colors[color] || '#888';
}
